import React, { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Typography } from '@mui/material';
import AiConfirmCard from 'app/shared/components/AiConfirmCard';
import PropTypes from 'prop-types';

import { AiProcessStepConfirmationDecision, ToolSlaGroupMode } from 'constants/aiProcessRuns';

import { confirmStepExecutionAction } from 'store/aiProcessRuns/actions';
import { selectIsConfirmingStep, selectStepPendingToolCall } from 'store/aiProcessRuns/selectors';

import {
  STEP_CONFIRM_LABELS,
  STEP_CONFIRMATION_LABELS,
  StepConfirmationTestIds,
} from './StepConfirmationActions.constants';
import {
  adjustSummaryStyles,
  containerStyles,
  promptStyles,
} from './StepConfirmationActions.styles';
import StepPendingToolCall from './StepPendingToolCall';
import useConditionReview from './StepPendingToolCall/hooks/useConditionReview';
import useResolvedLibraryConditions from './StepPendingToolCall/hooks/useResolvedLibraryConditions';
import { buildStepPendingToolCallView } from './StepPendingToolCall/StepPendingToolCall.utils';

const EMPTY_CONDITIONS = [];

const StepConfirmationActions = ({ stepExecutionId }) => {
  const dispatch = useDispatch();
  const isConfirming = useSelector(selectIsConfirmingStep(stepExecutionId));
  const pendingToolCall = useSelector(selectStepPendingToolCall(stepExecutionId));

  const view = useMemo(() => buildStepPendingToolCallView(pendingToolCall), [pendingToolCall]);
  const adhocConditions = view?.conditions ?? EMPTY_CONDITIONS;
  const libraryConditions = view?.libraryConditions ?? EMPTY_CONDITIONS;
  const resolvedLibraryConditions = useResolvedLibraryConditions(libraryConditions);
  const conditions = adhocConditions.length > 0 ? adhocConditions : resolvedLibraryConditions;
  const hasConditions = conditions.length > 0;
  const review = useConditionReview(conditions);

  const slaGroup = pendingToolCall?.slaGroup ?? null;
  const isSlaSelectionRequired =
    slaGroup?.mode === ToolSlaGroupMode.PROMPT_USER && slaGroup?.selectionRequired === true;

  const labels = useMemo(
    () => ({
      ...STEP_CONFIRM_LABELS,
      approve: hasConditions
        ? STEP_CONFIRMATION_LABELS.CONFIRM_AND_ADD({ count: review.summary.addCount })
        : STEP_CONFIRM_LABELS.approve,
      dismiss: hasConditions ? STEP_CONFIRMATION_LABELS.REJECT_ALL : STEP_CONFIRM_LABELS.dismiss,
    }),
    [hasConditions, review.summary.addCount],
  );

  const adjustSummary =
    hasConditions && review.hasAnyAction
      ? STEP_CONFIRMATION_LABELS.ADJUST_SUMMARY(review.summary)
      : null;

  const onSubmit = useCallback(
    selectedSlaGroupId => {
      dispatch(
        confirmStepExecutionAction({
          stepExecutionId,
          decision: AiProcessStepConfirmationDecision.CONFIRMED,
          reason: null,
          selectedSlaGroupId: isSlaSelectionRequired ? selectedSlaGroupId : null,
          conditionDecisions: review.buildConditionDecisions(),
        }),
      );
    },
    [dispatch, isSlaSelectionRequired, review, stepExecutionId],
  );

  const onDismiss = useCallback(
    reason => {
      dispatch(
        confirmStepExecutionAction({
          stepExecutionId,
          decision: AiProcessStepConfirmationDecision.REJECTED,
          reason,
        }),
      );
    },
    [dispatch, stepExecutionId],
  );

  return (
    <Box data-testid={StepConfirmationTestIds.CONTAINER} sx={containerStyles}>
      <StepPendingToolCall
        conditions={conditions}
        review={review}
        stepExecutionId={stepExecutionId}
      />
      {!isSlaSelectionRequired ? (
        <Typography sx={promptStyles} variant="regular12">
          {STEP_CONFIRMATION_LABELS.PROMPT}
        </Typography>
      ) : null}
      {adjustSummary ? (
        <Typography
          data-testid={StepConfirmationTestIds.ADJUST_SUMMARY}
          sx={adjustSummaryStyles}
          variant="regular12"
        >
          {adjustSummary}
        </Typography>
      ) : null}
      <AiConfirmCard
        isSlaSelectionRequired={isSlaSelectionRequired}
        isSubmitting={isConfirming}
        labels={labels}
        onDismiss={onDismiss}
        onSubmit={onSubmit}
      />
    </Box>
  );
};

StepConfirmationActions.propTypes = {
  stepExecutionId: PropTypes.string.isRequired,
};

export default memo(StepConfirmationActions);
